import { useState, useCallback, useEffect, type FormEvent } from 'react';
import { ApiClientError } from '../services/api-client';
import { apiClient } from '../services/api-client';
import { addProtectivePut } from '../services/protective-put-service';
import {
  validateAddProtectivePutInput,
  selectSchedule,
  computeCommission,
  InstrumentClass,
  OPTION_CONTRACT_MULTIPLIER,
  type ValidationError,
} from '@covered-calls/shared';
import type {
  BrokerProfile,
  AddProtectivePutInput,
  CommissionResult,
  Exchange,
} from '@covered-calls/shared';

/**
 * AddProtectivePutDialog — attach a long protective put to an existing open
 * stock holding (the "separate transaction" path). Posts to
 * POST /api/trades/{id}/protective-put via addProtectivePut.
 *
 * The put premium is a DEBIT (paid). Contracts are derived from the position
 * quantity (quantity / 100); the backend re-validates this. Commission is
 * resolved from the (exchange, OPTIONS) schedule with a GST split preview.
 *
 * Validates: Requirements 2.1, 2.2, 2.3
 */

export interface AddProtectivePutDialogProps {
  positionId: string;
  ticker: string;
  quantity: number;
  /** The exchange the underlying trades on; keys the commission schedule selection. */
  exchange: Exchange;
  /** Optional pre-loaded broker profiles; fetched on mount when omitted. */
  brokerProfiles?: BrokerProfile[];
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormState {
  putStrikePrice: string;
  putPremium: string;
  putExpiryDate: string;
  putCommission: string;
  brokerProfileId: string;
}

const initialFormState: FormState = {
  putStrikePrice: '',
  putPremium: '',
  putExpiryDate: '',
  putCommission: '',
  brokerProfileId: '',
};

export function AddProtectivePutDialog({
  positionId,
  ticker,
  quantity,
  exchange,
  brokerProfiles: initialProfiles,
  onSuccess,
  onCancel,
}: AddProtectivePutDialogProps) {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [brokerProfiles, setBrokerProfiles] = useState<BrokerProfile[]>(initialProfiles ?? []);
  // Commission/GST split from the shared calculator for the put (OPTIONS) leg.
  const [commissionBreakdown, setCommissionBreakdown] = useState<CommissionResult | null>(null);
  // True when a profile is selected but has no schedule for this (exchange, OPTIONS) cell.
  const [noScheduleForLeg, setNoScheduleForLeg] = useState(false);

  // Contracts are fixed by the holding size (1 contract per 100 shares).
  const contracts = Math.floor(quantity / OPTION_CONTRACT_MULTIPLIER);

  // Fetch broker profiles on mount when not supplied by the parent.
  useEffect(() => {
    if (initialProfiles && initialProfiles.length > 0) {
      return;
    }
    async function loadProfiles() {
      try {
        const profiles = await apiClient.get<BrokerProfile[]>('/broker-profiles');
        setBrokerProfiles(profiles);
      } catch {
        // Non-critical: continue without profiles
      }
    }
    loadProfiles();
  }, [initialProfiles]);

  /**
   * Resolve the put-leg commission via the shared calculator.
   *
   * Selects the (exchange, OPTIONS) schedule and computes the commission/GST
   * split using tradeValue = putPremium × quantity. Populates the commission
   * field with the effective total and records the split for the preview. When
   * the profile has no schedule for this leg, falls back to manual entry.
   */
  const resolveCommission = useCallback(
    (profileId: string, premiumStr: string) => {
      if (!profileId) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }
      const profile = brokerProfiles.find((p) => p.profileId === profileId);
      if (!profile) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(false);
        return;
      }
      const schedule = selectSchedule(profile, exchange, InstrumentClass.OPTIONS);
      if (!schedule) {
        setCommissionBreakdown(null);
        setNoScheduleForLeg(true);
        return;
      }
      const premium = parseFloat(premiumStr) || 0;
      const tradeValue = premium * quantity;
      const breakdown = computeCommission(schedule, { tradeValue, quantity });
      setNoScheduleForLeg(false);
      setCommissionBreakdown(breakdown);
      setForm((prev) => ({ ...prev, putCommission: breakdown.total.toFixed(2) }));
    },
    [brokerProfiles, exchange, quantity]
  );

  const handleFieldChange = useCallback(
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => prev.filter((err) => err.field !== field));
      setServerError(null);
      // Premium drives the put trade value; recompute when it changes and a
      // broker profile is already selected.
      if (field === 'putPremium' && form.brokerProfileId) {
        resolveCommission(form.brokerProfileId, value);
      }
    },
    [resolveCommission, form.brokerProfileId]
  );

  const handleBrokerProfileChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const profileId = e.target.value;
      setForm((prev) => ({ ...prev, brokerProfileId: profileId }));
      resolveCommission(profileId, form.putPremium);
      setServerError(null);
    },
    [resolveCommission, form.putPremium]
  );

  const getFieldError = (field: string): string | undefined => {
    return errors.find((err) => err.field === field)?.message;
  };

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setServerError(null);

      const input: AddProtectivePutInput = {
        putStrikePrice: parseFloat(form.putStrikePrice),
        putPremium: parseFloat(form.putPremium),
        putExpiryDate: form.putExpiryDate,
        putContracts: contracts,
        putCommission: form.putCommission.trim() !== '' ? parseFloat(form.putCommission) : undefined,
        brokerProfileId: form.brokerProfileId || undefined,
      };

      const validationErrors = validateAddProtectivePutInput(input);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors([]);
      setIsSubmitting(true);

      try {
        await addProtectivePut(positionId, input);
        onSuccess();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (error.status === 409) {
            setServerError(
              'This position already has an active protective put. You cannot add another until the current one is closed, expires, or is exercised.'
            );
          } else if (error.errors && error.errors.length > 0) {
            setErrors(error.errors.map((err) => ({ field: err.field, message: err.message })));
          } else {
            setServerError(error.message || 'An error occurred. Please try again.');
          }
        } else {
          setServerError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, positionId, contracts, onSuccess]
  );

  return (
    <div
      role="dialog"
      aria-labelledby="add-put-dialog-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2 id="add-put-dialog-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Add Protective Put — {ticker}
        </h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          Buy a long put to protect your {quantity.toLocaleString()} shares of {ticker}
          {' '}({contracts} contract{contracts === 1 ? '' : 's'}). The premium is paid (a debit).
        </p>

        {serverError && (
          <div
            role="alert"
            style={{
              backgroundColor: 'var(--color-chip-danger-bg)',
              color: 'var(--color-chip-danger-text)',
              padding: '0.75rem 1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Strike Price */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="add-put-strike" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
              Put Strike Price ($)
            </label>
            <input
              id="add-put-strike"
              type="number"
              step="0.01"
              min="0.01"
              max="999999.99"
              value={form.putStrikePrice}
              onChange={handleFieldChange('putStrikePrice')}
              aria-invalid={!!getFieldError('putStrikePrice')}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('putStrikePrice') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('putStrikePrice') && (
              <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{getFieldError('putStrikePrice')}</span>
            )}
          </div>

          {/* Premium (paid) */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="add-put-premium" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
              Premium (paid) ($ per share)
            </label>
            <input
              id="add-put-premium"
              type="number"
              step="0.01"
              min="0.00"
              max="999999.99"
              value={form.putPremium}
              onChange={handleFieldChange('putPremium')}
              aria-invalid={!!getFieldError('putPremium')}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('putPremium') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('putPremium') && (
              <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{getFieldError('putPremium')}</span>
            )}
          </div>

          {/* Expiry Date */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="add-put-expiry" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
              Expiry Date
            </label>
            <input
              id="add-put-expiry"
              type="date"
              value={form.putExpiryDate}
              onChange={handleFieldChange('putExpiryDate')}
              aria-invalid={!!getFieldError('putExpiryDate')}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('putExpiryDate') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('putExpiryDate') && (
              <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{getFieldError('putExpiryDate')}</span>
            )}
          </div>

          {/* Contracts (read-only, derived from holding) */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>Contracts</label>
            <input
              type="text"
              value={`${contracts} (from ${quantity.toLocaleString()} shares)`}
              readOnly
              disabled
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-input-border)',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                backgroundColor: 'var(--color-surface-2)',
                color: 'var(--color-muted)',
              }}
            />
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              One contract covers {OPTION_CONTRACT_MULTIPLIER} shares; fixed by your holding size.
            </span>
          </div>

          {/* Broker Profile Selector */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="add-put-broker-profile" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
              Broker Profile (optional)
            </label>
            <select
              id="add-put-broker-profile"
              value={form.brokerProfileId}
              onChange={handleBrokerProfileChange}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-input-border)',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                backgroundColor: 'var(--color-surface)',
              }}
            >
              <option value="">-- Select broker profile --</option>
              {brokerProfiles.map((profile) => (
                <option key={profile.profileId} value={profile.profileId}>
                  {profile.brokerName}
                </option>
              ))}
            </select>
          </div>

          {/* Commission */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="add-put-commission" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
              Put Commission ($)
            </label>
            <input
              id="add-put-commission"
              type="number"
              step="0.01"
              min="0.00"
              max="99999.99"
              value={form.putCommission}
              onChange={handleFieldChange('putCommission')}
              aria-invalid={!!getFieldError('putCommission')}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('putCommission') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('putCommission') && (
              <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{getFieldError('putCommission')}</span>
            )}

            {/* Commission / GST split from the shared calculator */}
            {commissionBreakdown && (
              <div
                data-testid="add-put-commission-breakdown"
                style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'var(--color-surface-2)',
                  borderRadius: '4px',
                  fontSize: '0.8125rem',
                  color: 'var(--color-text)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Commission</span>
                  <span>${commissionBreakdown.commission.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>GST</span>
                  <span>${commissionBreakdown.gst.toFixed(2)}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 600,
                    borderTop: '1px solid var(--color-border)',
                    marginTop: '0.25rem',
                    paddingTop: '0.25rem',
                  }}
                >
                  <span>Total</span>
                  <span>${commissionBreakdown.total.toFixed(2)}</span>
                </div>
              </div>
            )}

            {noScheduleForLeg && (
              <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-chip-warning-text)', marginTop: '0.25rem' }}>
                This broker profile has no schedule for {exchange} options. Enter the commission manually.
              </span>
            )}

            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              Auto-populated from broker profile. Override if actual commission differs.
            </span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-muted)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Adding...' : 'Add Protective Put'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
